"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Snowflake, Compass } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Transit Compression & Short Length-of-Stay",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Agra faces high transit turnover with same-day Taj Mahal tourists. We implement dynamic multi-night packaging, late check-out incentives, and experience-led pricing to convert day-trippers into high-yield overnight guests.",
  metricLabel: "Strategy focus",
  metricValue: "Length-of-stay controls & experiential package yield"
}, {
  id: 2,
  title: "Comp-Set Undercutting on the Golden Triangle",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Rapid price drops by competing properties across Fatehabad Road and near the Taj East Gate erode margins. Automated rate-shopper triggers protect your ADR and capitalize on sudden weekend surges from Delhi-NCR.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic competitor benchmarking & surge pricing"
}, {
  id: 3,
  title: "B2B Wholesaler Leakage & OTA Commission Traps",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Unmanaged group tour operator allocations frequently leak discounted rates onto public OTAs. We establish strict B2B rate fencing and drive direct web bookings through Google Hotel Ads and geo-targeted campaigns.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityPeriods = [{
  season: "Peak Heritage Season",
  months: "October – March",
  icon: Snowflake,
  focus: "High inbound foreign & luxury leisure",
  action: "Maximize ADR with strict non-refundable fencing and minimum 2-night stay controls during peak sunrise slots."
}, {
  season: "Summer & Corporate Window",
  months: "April – June",
  icon: Sun,
  focus: "MICE, local banquets & value corporate stays",
  action: "Drive volume with day-delegate packages, staycation deals, and dynamic flash promotions across regional OTAs."
}, {
  season: "Monsoon & Long Weekends",
  months: "July – September",
  icon: CloudRain,
  focus: "Delhi-NCR drive-in tourists & domestic weekenders",
  action: "Deploy 48-hour pace-based pricing, expressway bundled packages, and value-add food & beverage inclusions."
}, {
  season: "Year-Round Transit & Heritage",
  months: "Continuous Demand",
  icon: Compass,
  focus: "Golden Triangle circuit & heritage travellers",
  action: "Optimize Google Free Booking Links and direct mobile booking engine channels to capture last-minute bookings."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeagra" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Agra
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Agra presents a unique micro-market shaped by heavy Golden Triangle transit traffic, high day-tripper compression, and sharp seasonality between peak heritage winter months and summer shoulder periods. Sunil Singh crafts data-driven revenue models engineered specifically for Agra hotels, heritage havelis, and boutique luxury properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-14 md:mt-18 rounded-xl border border-border bg-card/60 p-6 md:p-8">
          <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-widest text-secondary">
                Agra Demand Cycles
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-1">
                Seasonality & Yield Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-lg">
              Structured pace strategies calibrated for Agra&apos;s dramatic seasonal shifts and sudden Golden Triangle weekend compressions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 pt-2">
            {seasonalityPeriods.map((period, index) => {
            const IconComponent = period.icon;
            return <div key={period.season} data-index={index} className="rounded-lg border border-border/70 bg-background/80 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="inline-flex size-9 items-center justify-center rounded-md bg-secondary/15 text-secondary-foreground">
                        <IconComponent className="size-4 text-chart-2" />
                      </div>
                      <Badge variant="outline" className="text-[11px] font-mono border-border">
                        {period.months}
                      </Badge>
                    </div>
                    <h4 className="font-serif font-bold text-base text-foreground mb-1">
                      {period.season}
                    </h4>
                    <p className="text-xs font-medium text-secondary mb-2.5">
                      {period.focus}
                    </p>
                  </div>
                  <p className="text-xs leading-relaxed text-muted-foreground pt-2 border-t border-border/50">
                    {period.action}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Agra hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}